import React, { useCallback, useEffect, useState } from 'react';

import Button from 'epn-ui/umd/dist/components/Button';
import Dialog from 'epn-ui/umd/dist/components/Dialog';
import Input from 'epn-ui/umd/dist/components/InputText';
import Loader from 'epn-ui/umd/dist/components/Loader';

import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import debounce from 'lodash/debounce';
import { toast } from 'react-toastify';

import BaseContainer from '@base/BaseContainer';
import BaseGrid from '@base/BaseGrid';
import BaseToast from '@base/BaseToast';

import api from '@api';
import { IGetBotTokenDataAttributes } from '@api/types';
import apiHooks, { ICreateUseRequestResponse } from '@apiHooks';
import useI18n from '@i18n';
import isObject from '@utils/chekers/isObject';
import copyTextToClipboard from '@utils/clipboard';
import styles from './styles.module.scss';

const { useBotToken } = apiHooks;

interface IRolesBlock {
    auth?: boolean;
    goAuth: () => void;
}

const RolesBlock: React.FC<IRolesBlock> = ({ auth = false, goAuth }) => {
    const { t } = useI18n('TelegramBot');
    const { t: tCommon } = useI18n('Common');
    const { t: tErrors } = useI18n('Errors');

    const [modal, showModal] = useState<boolean>(false);
    const { data: botTokenData, isLoading, error, mutate: refreshLink } = useBotToken({
        condition: modal,
    }) as ICreateUseRequestResponse<IGetBotTokenDataAttributes | Record<string, void>>;

    const { url: link } = botTokenData || {};
    const isEmptyTokensResponce =
        (isObject(botTokenData) && Object.keys(botTokenData).length === 0) || error;

    const onShowModal = (): void => {
        if (!auth) {
            goAuth();
            return;
        }
        showModal(true);
    };

    const setBotToken = useCallback(async (): Promise<void> => {
        try {
            await api.bot.setToken({});
            refreshLink(null, true);
        } catch (err) {
            console.error(err);
        }
    }, [refreshLink]);

    useEffect((): void => {
        if (!botTokenData && botTokenData === null && !isLoading) {
            setBotToken();
        }
    }, [botTokenData, setBotToken, isLoading]);

    const onCopy = debounce(async () => {
        if (!link) return;

        try {
            await copyTextToClipboard(link);
            toast.success(<BaseToast text={tCommon('Copied')} type="success" />, {
                toastId: link,
            });
        } catch (err) {
            toast.error(<BaseToast text={tCommon('Error')} type="error" />, {
                toastId: link,
            });
        }
    }, 200);

    return (
        <BaseContainer>
            <BaseGrid container className="flex-wrap mb-[104px]">
                <BaseGrid item md={6} initial={12}>
                    <div className={styles.row}>
                        <h3 className="font-medium text-h1 text-brandBlue-500 mt-[63px] md:text-3xl">
                            {t('Affiliate')}
                        </h3>
                        <p className="text-base text-brandBlue-300 py-[16px]">
                            1. {t('Get the link to our Telegram bot')}
                        </p>
                        <Button
                            type="primary"
                            size="small"
                            disabled={isLoading}
                            onClick={onShowModal}
                        >
                            {t('Get the link')}
                        </Button>
                    </div>
                    <div className={styles.row}>
                        <p className="text-base text-brandBlue-300 py-[16px]">
                            2.{' '}
                            {t(
                                'Send the link in your telegram storage chat with yourself and then follow it',
                            )}
                        </p>
                    </div>
                    <div className={styles.row}>
                        <p className="text-base text-brandBlue-300 py-[16px]">
                            3. {t('In the chat window with the bot, click')}{' '}
                            <strong className="font-bold text-[#f87171]">Start</strong>
                        </p>
                    </div>
                </BaseGrid>
                <BaseGrid item md={6} xs={12} initial={12}>
                    <div className={styles.row}>
                        <h3 className="font-medium text-h1 text-[#1E293B] mt-[63px] md:text-3xl">
                            {t('Assistants')}
                        </h3>
                        <p className="text-base text-[#475569] py-[16px]">
                            {t('Connect assistants to the telegram bot')}
                        </p>
                    </div>
                    <div className={styles.row}>
                        <p className="text-base text-[#475569] py-[16px]">
                            {t(
                                'To connect the assistants, you should just give them a special link to connect',
                            )}
                        </p>
                        <Button
                            type="primary"
                            size="small"
                            disabled={isLoading}
                            onClick={onShowModal}
                        >
                            {t('Get the link')}
                        </Button>
                    </div>
                </BaseGrid>
            </BaseGrid>
            {modal && (
                <Dialog
                    title={t('Copy the link to the bot')}
                    onCancel={() => showModal(false)}
                    open
                >
                    <div id="ModalCopyBotLink">
                        {isLoading || !link ? (
                            <Loader />
                        ) : (
                            <Input
                                value={link}
                                readOnly
                                suffix={
                                    <FontAwesomeIcon
                                        icon={faCopy}
                                        className="cursor-pointer"
                                        onClick={onCopy}
                                    />
                                }
                            />
                        )}

                        {!isLoading && isEmptyTokensResponce && (
                            <div className="text-center text-red-500 text-p3">
                                {tErrors('Something went wrong')}.&nbsp;
                                {t('Try again or contact technical support')}.
                            </div>
                        )}
                    </div>
                </Dialog>
            )}
        </BaseContainer>
    );
};

export default RolesBlock;
